import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clapperboard, Check, X, Loader2, Film, Eye, Pencil } from "lucide-react";
import { api, type StoryboardView, type StoryboardPlanJobView } from "../../lib/api";

/**
 * The storyboard panel — a plan you read before any money is spent, and a reel
 * you watch before anything is published.
 *
 * WHY IT EXISTS. The video button used to go from an article straight to
 * footage: a fresh shot list was written and filmed in one press, and the first
 * human to see any of those decisions was looking at two to three dollars of
 * generated clips. One real run put a bowl of dried herbs and a folded towel on
 * screen while the narration named contraindications.
 *
 * TWO GATES, deliberately two moments.
 *   1. Approve the plan  → unlocks footage.  Protects the money.
 *   2. Release the reel   → unlocks publishing. Protects the audience.
 * Approving a shot table on paper and watching what it produced are different
 * judgements. Collapsing them is how a reel nobody watched reaches Instagram.
 */

const STATUS_LABEL: Record<StoryboardView["status"], string> = {
  draft: "ממתין לאישור",
  approved: "מאושר — אפשר לצלם",
  filmed: "צולם — ממתין לצפייה",
  released: "שוחרר לפרסום",
  rejected: "נדחה",
};

const STATUS_CLASS: Record<StoryboardView["status"], string> = {
  draft: "bg-amber-100 text-amber-900",
  approved: "bg-brand-100 text-brand-800",
  filmed: "bg-sky-100 text-sky-900",
  released: "bg-emerald-100 text-emerald-900",
  rejected: "bg-neutral-200 text-neutral-700",
};

function StatusPill({ status }: { status: StoryboardView["status"] }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CLASS[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

/**
 * The shot table. This is the artifact the operator actually reads, so it shows
 * what the shot SAYS beside what it SHOWS — the herbs-over-contraindications
 * failure is invisible in a list of prompts and obvious in this pairing.
 */
/**
 * One narration line, editable while the plan is a draft.
 *
 * WHY EDITABLE AT ALL. A plan that is three words over its budget, or has one
 * line that says the wrong thing, used to leave two options: film it, or pay
 * for a whole new plan and lose everything else that was right. The smallest
 * edit had no path.
 *
 * Draft only — approving is what unlocks the money, so what gets filmed has to
 * be what somebody read. Editing an approved plan would make the gate a
 * formality.
 */
function NarrationCell({
  board, shotIndex, text, editable, onSaved, onDirtyChange,
}: {
  board: StoryboardView;
  shotIndex: number;
  text: string;
  editable: boolean;
  onSaved: () => void;
  /** Lifted so the approve button can refuse while a line is unsaved. */
  onDirtyChange: (shotIndex: number, dirty: boolean) => void;
}) {
  const [value, setValue] = useState(text);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => { setValue(text); }, [text]);

  const save = useMutation({
    mutationFn: () => { setError(null); setNote(null); return api.editStoryboardNarration(board.id, shotIndex, value.trim()); },
    onSuccess: (r) => {
      setNote([...(r.notes ?? []), ...(r.issues ?? []).map((i) => i.message)].join(" · ") || "נשמר");
      onSaved();
    },
    onError: (e) => setError((e as Error).message),
  });

  const words = value.trim().split(/\s+/).filter(Boolean).length;
  const dirty = value.trim() !== text.trim();

  /**
   * THE EDIT THAT WAS LOST.
   *
   * This cell held the typed line in local state and offered a 10px "שמירה"
   * button that only appeared once the line was dirty. Nothing else knew the
   * line had been touched. So on 2026-10-21 an operator edited several lines,
   * pressed אישור, and the approve invalidated the query, the server text came
   * back unchanged, and the `useEffect` below overwrote every edit. The plan
   * was filmed-ready with none of the corrections in it and the screen never
   * said a word — `updated_at` on the row was equal to `approved_at` to the
   * millisecond, which is the only reason it was provable afterwards.
   *
   * Two changes, because either alone still loses work:
   *   - leaving the field saves it, so the button is a shortcut and not the
   *     only path;
   *   - the dirty flag goes UP, so approving refuses while anything is unsaved.
   */
  useEffect(() => {
    onDirtyChange(shotIndex, dirty && value.trim().length > 0);
  }, [dirty, value, shotIndex, onDirtyChange]);

  const saveOnBlur = () => {
    if (!dirty || value.trim().length === 0 || save.isPending) return;
    save.mutate();
  };

  if (!editable) return <span dir="auto">{text || "—"}</span>;

  return (
    <div className="space-y-1">
      <textarea
        value={value}
        dir="auto"
        rows={2}
        onChange={(e) => setValue(e.target.value)}
        onBlur={saveOnBlur}
        className="w-full min-w-[16rem] rounded border border-line bg-canvas px-2 py-1 text-xs leading-relaxed"
      />
      <div className="flex items-center gap-2 text-[10px] text-muted">
        <span>{words} מילים</span>
        {dirty && value.trim().length > 0 && !save.isPending && (
          <span className="text-amber-700">לא נשמר</span>
        )}
        {dirty && (
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || value.trim().length === 0}
            className="rounded border border-brand-700 px-1.5 py-0.5 text-[10px] text-brand-700 disabled:opacity-40"
          >
            {save.isPending ? "שומר…" : "שמירה"}
          </button>
        )}
        {note && <span className="text-emerald-700">{note}</span>}
        {error && <span className="text-danger">{error}</span>}
      </div>
    </div>
  );
}

function ShotTable({ board, onEdited, onDirtyChange }: {
  board: StoryboardView;
  onEdited: () => void;
  onDirtyChange: (shotIndex: number, dirty: boolean) => void;
}) {
  const shots = board.script.shots ?? [];
  const narration = board.script.narration ?? [];
  let clock = 0;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-right text-xs">
        <thead>
          <tr className="border-b border-line text-[11px] text-muted">
            <th className="px-2 py-1.5 font-medium">זמן</th>
            <th className="px-2 py-1.5 font-medium">תפקיד</th>
            <th className="px-2 py-1.5 font-medium">אומר</th>
            <th className="px-2 py-1.5 font-medium">מראה</th>
            <th className="px-2 py-1.5 font-medium">עושה</th>
            <th className="px-2 py-1.5 font-medium">איפה</th>
            <th className="px-2 py-1.5 font-medium">מצלמה</th>
          </tr>
        </thead>
        <tbody>
          {shots.map((shot) => {
            const line = narration.find((n) => n.shotIndex === shot.index);
            const at = clock;
            clock += shot.seconds;
            return (
              <tr key={shot.index} className="border-b border-line/60 align-top">
                <td className="whitespace-nowrap px-2 py-2 text-muted">
                  {at.toFixed(0)}s
                  <span className="block text-[10px] opacity-70">{shot.seconds}s</span>
                </td>
                <td className="whitespace-nowrap px-2 py-2">
                  <span className="rounded bg-canvas px-1.5 py-0.5 text-[10px]">{shot.tags.role}</span>
                  <span className="block pt-1 text-[10px] text-muted">{shot.mode}</span>
                </td>
                <td className="px-2 py-2 text-start font-medium">
                  <NarrationCell
                    board={board}
                    shotIndex={shot.index}
                    text={line?.text ?? ""}
                    editable={board.status === "draft"}
                    onSaved={onEdited}
                    onDirtyChange={onDirtyChange}
                  />
                </td>
                <td dir="auto" className="px-2 py-2 text-start">{shot.spec.subject}</td>
                <td dir="auto" className="px-2 py-2 text-start">{shot.spec.action}</td>
                <td dir="auto" className="px-2 py-2 text-start text-muted">{shot.spec.environment}</td>
                <td className="whitespace-nowrap px-2 py-2 text-muted">
                  {shot.spec.cameraMove}
                  <span className="block text-[10px]">{shot.spec.shotSize}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BoardCard({
  board,
  keywordId,
  onFilm,
  filming,
}: {
  board: StoryboardView;
  keywordId: string;
  onFilm: (id: number) => void;
  filming: boolean;
}) {
  const qc = useQueryClient();
  // Open on the states that WANT something from the operator: a draft to read
  // and approve, and a filmed reel to watch before gate 2. A filmed board
  // arriving collapsed hid the player behind a click nobody knew to make.
  const [open, setOpen] = useState(board.status === "draft" || board.status === "filmed");
  const [rejecting, setRejecting] = useState(false);
  /**
   * Narration lines typed but not saved. Approving while this is non-empty
   * silently discarded the edits — see the long note in `NarrationCell`.
   */
  const [unsaved, setUnsaved] = useState<number[]>([]);
  const onDirtyChange = useCallback((shotIndex: number, dirty: boolean) => {
    setUnsaved((prev) => {
      const has = prev.includes(shotIndex);
      if (dirty === has) return prev;
      return dirty ? [...prev, shotIndex] : prev.filter((i) => i !== shotIndex);
    });
  }, []);
  const [reason, setReason] = useState("");

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["storyboards", keywordId] });

  const approve = useMutation({ mutationFn: () => api.approveStoryboard(board.id), onSuccess: invalidate });
  const unapprove = useMutation({ mutationFn: () => api.unapproveStoryboard(board.id), onSuccess: invalidate });
  const reject = useMutation({
    mutationFn: () => api.rejectStoryboard(board.id, reason.trim() || "לא צוינה סיבה"),
    onSuccess: () => { setRejecting(false); invalidate(); },
  });
  const release = useMutation({ mutationFn: () => api.releaseStoryboard(board.id), onSuccess: invalidate });

  const shots = board.script.shots ?? [];
  const footage = shots.reduce((n, s) => n + s.seconds, 0);
  // What approving this actually spends. Measured FLUX rate; see
  // videos/model-catalog.ts, where every price was confirmed against a bill.
  // On the button, not in a tooltip: the whole point of the gate is that the
  // cost is known before the press, not discovered in the ledger afterwards.
  const footageUsd = footage * 0.06;

  return (
    <div className="rounded-xl border border-line bg-canvas-soft">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center gap-2 px-3 py-2.5 text-right"
      >
        <StatusPill status={board.status} />
        <span className="text-sm font-medium">
          {board.topicTitle ?? "המאמר כולו"}
        </span>
        <span className="text-xs text-muted">
          {board.shotCount} שוטים · {footage}s חומר · {board.totalWords} מילים
          {board.status === "draft" || board.status === "approved" ? ` · ~$${footageUsd.toFixed(2)} לצילום` : ""}
        </span>
        {board.warningCount > 0 && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-900">
            {board.warningCount} אזהרות
          </span>
        )}
        <span className="ms-auto text-[11px] text-muted">
          {new Date(board.createdAt).toLocaleString("he-IL")} · ${board.costUsd.toFixed(3)}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-line px-3 py-3">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted">
            <span>ליהוק: {board.script.casting}</span>
            {board.script.subjectState && <span>מצב: {board.script.subjectState}</span>}
            {board.script.hookCategory && <span>וו: {board.script.hookCategory}</span>}
          </div>

          {/* THE REEL ITSELF, where the gate asks about it. Gate 2 is "I watched
              this", and until now the only way to watch was to dig the asset URL
              out of the API by hand. */}
          {/* EVERY SHOT AS ONE PICTURE, above the player.
              Gate 2 is "I watched this", and watching is forty seconds. The
              sheet answers "is shot 4 black" in one glance, which is the
              question that actually keeps a reel sitting unapproved. Captured
              from the RENDERED composition, so unlike the stills gate's
              predictions it is what the reel really looks like. */}
          {board.contactSheetUrl && (board.status === "filmed" || board.status === "released") && (
            <a
              href={board.contactSheetUrl}
              target="_blank"
              rel="noreferrer"
              className="block space-y-1"
              title="כל שוט בריל, בתמונה אחת. לחיצה פותחת בגודל מלא."
            >
              <img
                src={board.contactSheetUrl}
                alt="כל השוטים של הריל המוגמר"
                className="w-full max-w-[260px] rounded-lg border border-line"
              />
              <span className="block text-[11px] text-muted underline">כל השוטים בתמונה אחת</span>
            </a>
          )}

          {board.assetUrl && (board.status === "filmed" || board.status === "released") && (
            <div className="space-y-1">
              <video
                src={board.assetUrl}
                controls
                playsInline
                preload="metadata"
                className="w-full max-w-[260px] rounded-lg border border-line bg-black"
              />
              <a
                href={board.assetUrl}
                target="_blank"
                rel="noreferrer"
                className="block text-[11px] text-muted underline"
              >
                פתיחה בחלון חדש
                {board.renderSeconds ? ` · ${Math.round(board.renderSeconds)} שניות` : ""}
              </a>
            </div>
          )}

          <ShotTable board={board} onEdited={invalidate} onDirtyChange={onDirtyChange} />

          {(board.script.warnings ?? []).length > 0 && (
            <ul className="space-y-1 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-900">
              {board.script.warnings.map((w, i) => (
                <li key={i} dir="auto">! {w}</li>
              ))}
            </ul>
          )}

          {/* WHO passed each gate, on screen. "Somebody approved this" is not a trail. */}
          <div className="space-y-0.5 text-[11px] text-muted">
            {board.approvedBy && (
              <p>אושר לצילום על ידי {board.approvedBy.email || board.approvedBy.id} · {new Date(board.approvedAt!).toLocaleString("he-IL")}</p>
            )}
            {board.filmedAt && (
              <p>צולם {new Date(board.filmedAt).toLocaleString("he-IL")}
                {board.renderSeconds ? ` · ${board.renderSeconds.toFixed(1)} שניות` : ""}</p>
            )}
            {board.releasedBy && (
              <p>שוחרר לפרסום על ידי {board.releasedBy.email || board.releasedBy.id} · {new Date(board.releasedAt!).toLocaleString("he-IL")}</p>
            )}
            {board.rejectedReason && <p>נדחה: {board.rejectedReason}</p>}
          </div>

          {unsaved.length > 0 && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
              {unsaved.length === 1
                ? "שורת קריינות אחת נערכה ולא נשמרה."
                : `${unsaved.length} שורות קריינות נערכו ולא נשמרו.`}
              {" "}צאו מהשדה או לחצו שמירה. האישור חסום עד אז, כדי שהעריכה לא תימחק.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {board.status === "draft" && (
              <>
                <button
                  onClick={() => approve.mutate()}
                  disabled={approve.isPending || unsaved.length > 0}
                  className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  title={unsaved.length > 0
                    ? "יש שורות קריינות שנערכו ולא נשמרו. אישור עכשיו היה מוחק אותן."
                    : "מאשר את התוכנית הזו. רק אחרי זה אפשר לקנות חומר."}
                >
                  {approve.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  אישור התוכנית (~${footageUsd.toFixed(2)})
                </button>
                <button
                  onClick={() => setRejecting((v) => !v)}
                  className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs"
                >
                  <X size={13} /> דחייה
                </button>
              </>
            )}

            {board.status === "approved" && (
              <>
                <button
                  onClick={() => onFilm(board.id)}
                  disabled={filming}
                  className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  title="מצלם בדיוק את התוכנית הזו. עולה כסף: קליפ לכל שוט."
                >
                  {filming ? <Loader2 size={13} className="animate-spin" /> : <Film size={13} />}
                  צילום התוכנית (~${footageUsd.toFixed(2)})
                </button>
                {/* Narration is editable in draft only, so an approved plan had
                    no correction at any price — re-planning is a fresh draw,
                    not an edit. This re-opens gate 1; it does not skip it. */}
                <button
                  onClick={() => unapprove.mutate()}
                  disabled={unapprove.isPending || filming}
                  className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs disabled:opacity-50"
                  title="מחזיר את התוכנית לטיוטה כדי לערוך קריינות. לא עולה כלום, וצריך לאשר שוב אחרי העריכה."
                >
                  {unapprove.isPending ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} />}
                  חזרה לטיוטה לעריכה
                </button>
              </>
            )}

            {board.status === "filmed" && (
              <button
                onClick={() => release.mutate()}
                disabled={release.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                title="צפית בריל המוגמר? רק אחרי זה הוריאנטים יכולים להתפרסם."
              >
                {release.isPending ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                צפיתי — שחרור לפרסום
              </button>
            )}

            {approve.isError && <span className="text-xs text-danger">{(approve.error as Error).message}</span>}
            {unapprove.isError && <span className="text-xs text-danger">{(unapprove.error as Error).message}</span>}
            {release.isError && <span className="text-xs text-danger">{(release.error as Error).message}</span>}
          </div>

          {rejecting && (
            <div className="flex gap-2">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="למה התוכנית לא טובה?"
                className="flex-1 rounded-lg border border-line bg-canvas px-2 py-1.5 text-xs"
              />
              <button
                onClick={() => reject.mutate()}
                disabled={reject.isPending}
                className="rounded-lg border border-danger px-3 py-1.5 text-xs text-danger disabled:opacity-50"
              >
                דחייה
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function StoryboardPanel({
  keywordId,
  onFilm,
  filming,
}: {
  keywordId: string;
  onFilm: (storyboardId: number) => void;
  filming: boolean;
}) {
  const q = useQuery({
    queryKey: ["storyboards", keywordId],
    queryFn: () => api.storyboards(keywordId),
  });
  const qc = useQueryClient();

  const topics = q.data?.topics ?? [];
  const boards = q.data?.storyboards ?? [];
  const [topicId, setTopicId] = useState<string>("");

  // For articles written before topics were extracted at production time. One
  // small call; afterwards the picker above has something to pick.
  const extract = useMutation({
    mutationFn: () => api.extractVideoTopics(keywordId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["storyboards", keywordId] }); },
  });
  const [shotCount, setShotCount] = useState(8);

  // PLANNING RUNS ON THE SERVER, AND THIS POLLS IT. It used to wait inside the
  // request: 90-140 seconds against a 60-second gateway, so every plan showed a
  // 504 while the server finished and paid — and pressing again paid twice.
  const [planJob, setPlanJob] = useState<StoryboardPlanJobView | null>(null);
  const planning = planJob?.status === "running";

  // Re-attach on open: a plan started before the panel was closed is shown as
  // running rather than offering the button again.
  useEffect(() => {
    void api.planStoryboardStatus(keywordId).then((r) => { if (r.job?.status === "running") setPlanJob(r.job); }).catch(() => {});
  }, [keywordId]);

  useEffect(() => {
    if (!planning) return;
    const t = setInterval(() => {
      void api.planStoryboardStatus(keywordId).then((r) => {
        if (!r.job) return;
        setPlanJob(r.job);
        if (r.job.status !== "running") void qc.invalidateQueries({ queryKey: ["storyboards", keywordId] });
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [planning, keywordId, qc]);

  const plan = useMutation({
    mutationFn: () =>
      api.planStoryboard(keywordId, { ...(topicId ? { topicId } : {}), shotCount }),
    onSuccess: (r) => setPlanJob(r.job),
    // 409 = a plan for this article is already running. Attach to it rather
    // than reporting an error for something that is, in fact, happening.
    onError: () => {
      void api.planStoryboardStatus(keywordId).then((r) => { if (r.job) setPlanJob(r.job); }).catch(() => {});
    },
  });

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">סטוריבורד</h3>

        {/* The article was broken into video topics when it was written. Picking
            one hands the writer that topic's sections instead of the whole
            piece, which is the difference between a reel about something and a
            reel about everything. */}
        <select
          value={topicId}
          onChange={(e) => setTopicId(e.target.value)}
          disabled={plan.isPending || planning}
          className="max-w-[22rem] flex-1 truncate rounded-lg border border-line bg-canvas px-2 py-1.5 text-xs"
          title="על איזה נושא מתוך המאמר הריל הזה?"
        >
          <option value="">המאמר כולו</option>
          {topics.map((t) => (
            <option key={t.id} value={t.id}>
              [{t.archetype}] {t.title}
            </option>
          ))}
        </select>

        <select
          value={shotCount}
          onChange={(e) => setShotCount(Number(e.target.value))}
          disabled={plan.isPending || planning}
          className="rounded-lg border border-line bg-canvas px-2 py-1.5 text-xs"
          title="כמה שוטים בריל. כל שוט נקנה באורך של השורה שהוא נושא."
        >
          {[5, 6, 7, 8, 9, 10].map((n) => (
            <option key={n} value={n}>{n} שוטים</option>
          ))}
        </select>

        <button
          onClick={() => plan.mutate()}
          disabled={plan.isPending || planning}
          className="flex items-center gap-1.5 rounded-lg border border-brand-700 px-3 py-1.5 text-xs font-medium text-brand-700 disabled:opacity-50"
          title="קריאת מודל אחת. בלי תמונות, בלי חומר, בלי לקנות כלום."
        >
          {plan.isPending || planning ? <Loader2 size={13} className="animate-spin" /> : <Clapperboard size={13} />}
          {planning ? "מתכנן… (כדקה וחצי, אפשר לעזוב)" : "תכנון ריל (~$0.15)"}
        </button>

        {planJob?.status === "error" && <span className="text-xs text-danger">התכנון נכשל: {planJob.error}</span>}
        {planJob?.status === "done" && planJob.storyboardId !== null && (
          <span className="text-xs text-emerald-700">
            תוכנית {planJob.storyboardId} מוכנה · ${planJob.costUsd.toFixed(3)}
          </span>
        )}
      </div>

      {topics.length === 0 && (
        <p className="flex flex-wrap items-center gap-2 text-[11px] text-muted">
          <span>
            למאמר הזה עוד לא חולצו נושאי וידאו, אז הכותב יקבל את המאמר כולו. זה עובד, וזה פחות ממוקד:
            תוכנית שנשענת על ארבעה חלקים במאמר היא סיכום שלו, לא סרטון.
          </span>
          <button
            onClick={() => extract.mutate()}
            disabled={extract.isPending}
            className="rounded-lg border border-line px-2 py-1 text-[11px] text-ink disabled:opacity-50"
            title="קריאת מודל קטנה אחת על הכותרות, לא על המאמר כולו."
          >
            {extract.isPending ? "מחלץ…" : "חילוץ נושאי וידאו (~$0.01)"}
          </button>
          {extract.isError && <span className="text-danger">{(extract.error as Error).message}</span>}
        </p>
      )}

      {boards.length === 0 ? (
        <p className="text-xs text-muted">
          אין עדיין תוכנית. תכנון הוא קריאת מודל אחת ולא קונה כלום — צילום דורש תוכנית מאושרת.
        </p>
      ) : (
        <div className="space-y-2">
          {boards.map((b) => (
            <BoardCard key={b.id} board={b} keywordId={keywordId} onFilm={onFilm} filming={filming} />
          ))}
        </div>
      )}
    </section>
  );
}
